import { useEffect, useRef, useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { INACTIVITY_LIMIT_MS } from "@shared/const";

const WARNING_BEFORE_MS = 2 * 60 * 1000;          // warn 2 minutes before logout

/**
 * HIPAA-required automatic session timeout.
 * Logs the user out after INACTIVITY_LIMIT_MS of no mouse/keyboard/touch activity.
 * Shows a warning dialog 2 minutes before logout so the user can extend the session.
 * Will NOT log the user out while an upload is actively in progress.
 */
export function useSessionTimeout(
  isAuthenticated: boolean,
  isUploadActive?: () => boolean,
) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const logoutMutation = trpc.auth.logout.useMutation();

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const doLogout = useCallback(() => {
    // Never interrupt an active upload — defer the logout check by 5 minutes
    // and keep retrying until the upload finishes.
    if (isUploadActive?.()) {
      clearAllTimers();
      setShowWarning(false);
      // Re-arm a short timer to check again once the upload is done
      warningTimerRef.current = setTimeout(() => {
        if (isUploadActive?.()) {
          // Still uploading — keep deferring silently
          doLogout();
        } else {
          // Upload finished — now actually log out
          clearAllTimers();
          setShowWarning(false);
          logoutMutation.mutate(undefined, {
            onSettled: () => { window.location.href = "/"; },
          });
        }
      }, 5 * 60 * 1000); // check again in 5 minutes
      return;
    }
    clearAllTimers();
    setShowWarning(false);
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        window.location.href = "/";
      },
    });
  }, [clearAllTimers, isUploadActive, logoutMutation]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      // If an upload is in progress, suppress the warning dialog and defer
      // the entire timeout cycle by WARNING_BEFORE_MS so we check again soon.
      if (isUploadActive?.()) {
        logoutTimerRef.current = setTimeout(() => {
          doLogout();
        }, WARNING_BEFORE_MS);
        return;
      }
      setShowWarning(true);
      setSecondsLeft(120);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) return 0;
          return s - 1;
        });
      }, 1000);
      logoutTimerRef.current = setTimeout(() => {
        doLogout();
      }, WARNING_BEFORE_MS);
    }, INACTIVITY_LIMIT_MS - WARNING_BEFORE_MS);
  }, [clearAllTimers, doLogout]);

  const extendSession = useCallback(() => {
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!isAuthenticated) return;

    startTimers();

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    const handleActivity = () => {
      if (!showWarning) startTimers();
    };

    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      clearAllTimers();
      events.forEach((e) => window.removeEventListener(e, handleActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { showWarning, secondsLeft, extendSession, doLogout };
}
